import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { AuthTokens, UserClaims } from '../services/authCacheManager.service';
import { AuthClientBuilder } from '../services/authClient.service';
import { AuthHttpError } from '../services/authHttp.service';
import { getUrl } from './AuthContext.utils';
import { authReducer } from './reducers/auth.reducer';

interface AuthContextProps {
  claims: UserClaims | null;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  error?: string;
  signIn: (
    username: string,
    password: string
  ) => Promise<AuthTokens | undefined>;
  signOut: () => void;
  refreshSession: () => Promise<AuthTokens | void>;
  setUserCache: AuthClientBuilder['setUserCache'];
  buildSignInUrl: (returnUrl: string) => string;
  buildCallbackUrl: (tokens: AuthTokens, cbUrl: string | null) => string;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

type AuthProviderProps = {
  signInUrl: string;
  callbackUrl: string;
  authClient: AuthClientBuilder;
  setUserCallback?: (user: UserClaims) => void;
  onUserMismatch?: () => void;
};

export const AuthProvider: React.FC<PropsWithChildren<AuthProviderProps>> = ({
  children,
  signInUrl,
  callbackUrl,
  authClient,
  setUserCallback,
  onUserMismatch,
}) => {
  const authClientRef = useRef(authClient);
  const isInitializedRef = useRef(false);
  const userClaimsRef = useRef<UserClaims | null>(null);

  const [state, dispatch] = useReducer(authReducer, {
    claims: null,
    isAuthenticating: true,
    isAuthenticated: false,
    error: null,
  });

  const updateUserClaims = useCallback(
    (claims: UserClaims) => {
      if (
        userClaimsRef.current &&
        userClaimsRef.current.userId !== claims.userId
      ) {
        onUserMismatch && onUserMismatch();
      }

      if (claims.idToken !== userClaimsRef.current?.idToken) {
        userClaimsRef.current = claims;

        setUserCallback && setUserCallback(claims);

        dispatch({ type: 'SET_USER_SUCCESS', payload: { claims } });
      }
    },
    [setUserCallback, onUserMismatch]
  );

  const signOutUser: AuthContextProps['signOut'] = useCallback(() => {
    authClientRef.current.signOut();
    dispatch({ type: 'SIGN_OUT' });
  }, []);

  const signInUser: AuthContextProps['signIn'] = useCallback(
    async (username, password) => {
      dispatch({ type: 'SIGN_IN_STARTED' });

      try {
        await authClientRef.current.signIn({ username, password });

        const authTokens = await authClientRef.current.getAuthTokens();
        if (authTokens.claims) {
          updateUserClaims(authTokens.claims);
        }

        return authTokens.tokens || undefined;
      } catch (error) {
        signOutUser();

        dispatch({
          type: 'SIGN_IN_FAILED',
          payload: {
            error:
              error instanceof AuthHttpError
                ? error.authError.error
                : 'Unknown Error',
          },
        });
      }
    },
    [signOutUser, updateUserClaims]
  );

  const refreshSession: AuthContextProps['refreshSession'] = useCallback(
    () =>
      authClientRef.current
        .getAuthTokens()
        .then(({ tokens, isExpired, claims }) => {
          if (tokens && claims) {
            updateUserClaims(claims);
            return tokens;
          }

          if (isExpired) {
            dispatch({ type: 'SIGN_OUT' });
          }
        })
        .catch(() => signOutUser()),
    [updateUserClaims, signOutUser]
  );

  const setUserCache: AuthContextProps['setUserCache'] = useCallback(
    (tokens) => authClientRef.current.setUserCache(tokens),
    []
  );

  const buildSignInUrl: AuthContextProps['buildSignInUrl'] = useCallback(
    (returnUrl) => {
      const cbUrl = getUrl(callbackUrl, 'callbackUrl');
      cbUrl.searchParams.set('returnUrl', returnUrl);

      const url = getUrl(signInUrl, 'signInUrl');
      url.searchParams.set('callbackUrl', cbUrl.toString());

      return url.toString();
    },
    [callbackUrl, signInUrl]
  );

  const buildCallbackUrl: AuthContextProps['buildCallbackUrl'] = useCallback(
    (tokens, cbUrl) => {
      const url = getUrl(
        cbUrl || `${window.location.origin}/auth/callback`,
        'cbUrl'
      );

      url.searchParams.set('tokens', btoa(JSON.stringify(tokens)));

      return url.toString();
    },
    []
  );

  useEffect(() => {
    const isInitialized = isInitializedRef.current;
    if (!isInitialized) {
      isInitializedRef.current = true;
      refreshSession();
    }
  }, [refreshSession]);

  useEffect(() => {
    const authClient = authClientRef.current;
    authClient.startEventListener();
    return () => {
      authClient.dispose();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        claims: state.claims,
        isAuthenticated: state.isAuthenticated,
        isAuthenticating: state.isAuthenticating,
        error: state.error || undefined,
        signIn: signInUser,
        signOut: signOutUser,
        refreshSession,
        setUserCache,
        buildSignInUrl,
        buildCallbackUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useUser must be used within a AuthProvider');
  }
  return context;
};
